import type { Address, Intro, ModalCompound, Outro, Page, Person, UILabelOptional, UILabel, ComponentValueAvailableImport, ComponentValueAvailableExport } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/library'

export enum ResponseType {
	UnknownError = 'unknown',
	NetworkError = 'network_error',
	NoResponseError = 'no_response',
	BackendError = 'backend_error',
	JSError = 'js_error'
}

export interface Branding {
	color: {
		primary: string
		secondary: string
	}
	
	logo: {
		primary: string
		secondary: string
	}
}

export interface Client {
	name: string
	short?: string
}

export interface Office {
	id: number|string
	name: string
}

export interface OrderResponse {
	token: string
	ticket_id: string|number
	persons: Person[]
	client: Client
	office?: Office|null
	task_type: string|null|undefined
	property_type: string|null|undefined
	takeover_date: number | { iso: string, timestamp: number } | null | undefined
	branding: Branding
	intro?: Intro
	outro?: Outro
	address: Address
	type: string
	external_id: string|number
	imports?: ComponentValueAvailableImport[]
	exports?: ComponentValueAvailableExport[]
}

export type UIResponse = UIResponseV2 | UIResponseV3

export interface UIResponseV2 {
	style: string
	language: string
	available_languages?: string[]
	pages: Page[]
	validate_page_label: UILabel
	back_button_label: UILabel
	jump_to_content_label?: UILabelOptional
	menu_button_label?: UILabelOptional
	create_pages_pdf?: boolean|null
	reset_protocol_label?: UILabelOptional
	reset_protocol_modal?: ModalCompound|null
	additional_information_label?: UILabelOptional
	additional_information_modal?: ModalCompound|null
	title?: UILabelOptional
	copyright_visible?: boolean
}

export const isUIResponseV2 = (sth: any): sth is UIResponseV2 => {
	if (!sth || typeof(sth) != 'object') {
		return false
	}
	
	return !isUIResponseV3(sth)
}

export interface UIResponseV3 {
	protocol_type: string
	style: string
	available_languages: string[]
	default_language: string
	create_pages_pdf: boolean
	labels: {
		validate_page: string
		back_button: string
		menu_button: string
		jump_to_content?: UILabelOptional
		// title: string
	}
	reset_protocol: {
		label: UILabel
		modal: ModalCompound
	} | null
	additional_information: {
		label: UILabel
		modal: ModalCompound
	} | null
	intro: Intro
	pages: Page[]
	outro: Outro
	copyright_visible: boolean
}

export const isUIResponseV3 = (sth: any): sth is UIResponseV3 => {
	if (!sth || typeof(sth) != 'object') {
		return false
	}
	
	return typeof(sth.labels) == 'object' && typeof(sth.intro) == 'object' && typeof(sth.outro) == 'object'
}

export interface CreateImageCollectionResponse {
	hash_id: string|number
	title?: string|null
	metadata?: Record<string, any>|null
	semantic?: string|null
}

export interface CreateImageResponse {
	hash_id: string|number
	url: string
	title?: string|null
	uploaded_at?: {
		timestamp: number
		iso: string
	} | null
}

export interface CreateFileResponse {
	hash_id: string|number
	url: string
}

export interface BankIDDataResponse {
	common_name: string
	person_number: string
	timestamp: number
}

/** @deprecated Use consent flow with {@link ElhubConsentElectricityPDFResponse} */
export interface ElectricityPDFResponse {
	protocol_file_id: string|number
	url: string
}

// Elhub Consents

export interface ElhubConsentResponse {
	type: string
}

/**
 * Response for when the elhub signature flow starts
 */
export interface ElhubConsentSessionResponse {
	type: 'signing'
	url: string
}

export const isElhubConsentSessionsResponse = (sth: any): sth is ElhubConsentSessionResponse =>
	sth && typeof(sth) == 'object' && sth.type == 'signing'

/**
 * Response for when the session was signed already
 */
export interface ElhubConsentAlreadySignedResponse {
	type: 'already_signed'
	message: string
}

export const isElhubConsentAlreadySignedResponse = (sth: any): sth is ElhubConsentAlreadySignedResponse =>
	sth && typeof(sth) == 'object' && sth.type == 'already_signed'

/**
 * Response for when the elhub consent returned a PDF directly
 */
export interface ElhubConsentElectricityPDFResponse {
	type: 'power_pdf'
	protocol_file_id: string|number
	url: string
}

export const isElhubConsentElectricityPDFResponse = (sth: any): sth is ElhubConsentElectricityPDFResponse =>
	sth && typeof(sth) == 'object' && sth.type == 'power_pdf'

/**
 * Signature data from a successful Elhub signature
 */
export interface ElhubConsentData {
	message: string,
	protocol_signatures: ElhubConsentSignature[]
}

export interface ElhubConsentSignature {
	id: string|number
	mpid: string
	elhub_consent_id: string
	signicat_status: string
	protocol_file_id: number
}

export interface ElhubConsentErrorResponse {
	/** Human-friendly message */
	error: string
	/** Technical code **/
	error_code?: string
	failures?: ElhubConsentFailure[]
	third_party?: ElhubConsentThirdPartyError[]
}

export const isElhubConsentErrorResponse = (sth: any): sth is ElhubConsentErrorResponse =>
	sth && typeof(sth) == 'object' && sth.error

export interface ElhubConsentFailure {
	meter: {
		meter_number: string
	}
	elhub_document_id: string
	details: string[]
	http_status_code: number
	body: string
}

export interface ElhubConsentThirdPartyError {
	provider: string
	status_code: number
	response_body: any
}

export enum ElhubConsentErrorCode {
	/** S3 key missing or submission not found */
	SUBMISSION_NOT_FOUND = 'SUBMISSION_NOT_FOUND',
	/** Invalid submission data */
	INVALID_SUBMISSION = 'INVALID_SUBMISSION',
	/** No power buyer found in submission */
	POWER_BUYER_NOT_FOUND = 'POWER_BUYER_NOT_FOUND',
	/** Power company or power product missing (required for PDF generation) */
	NO_POWER_COMPANY_OR_PRODUCT = 'NO_POWER_COMPANY_OR_PRODUCT',
	/** Missing required Elhub payload fields (buyerNationalId, meteringPointId, supplierName) before POST */
	ELHUB_PAYLOAD_INVALID = 'ELHUB_PAYLOAD_INVALID',
	/** No Elhub documents could be created */
	NO_ELHUB_DOCUMENTS = 'NO_ELHUB_DOCUMENTS',
	/** Signicat did not return signing URL */
	SIGNING_URL_MISSING = 'SIGNING_URL_MISSING',
	/** Redirect URL missing in request */
	NO_REDIRECT_URL = 'NO_REDIRECT_URL',
	/** Missing signicat_session_id or signicat_document_id */
	SIGNING_DATA_MISSING = 'SIGNING_DATA_MISSING',
	/** Failed to download signed PDF from Signicat (after retries) */
	SIGNING_DOWNLOAD_FAILED = 'SIGNING_DOWNLOAD_FAILED',
	/** Signicat API failure (401, 5xx after retry) */
	SIGNING_ERROR = 'SIGNING_ERROR',
	/** Failed to upload document to Signicat */
	SIGNING_UPLOAD_FAILED = 'SIGNING_UPLOAD_FAILED',
	/** Signicat did not return document ID */
	SIGNING_NO_DOCUMENT_ID = 'SIGNING_NO_DOCUMENT_ID',
	/** Signer not set before creating session */
	SIGNING_NO_SIGNER = 'SIGNING_NO_SIGNER',
	/** No documents added before creating session */
	SIGNING_NO_DOCUMENTS = 'SIGNING_NO_DOCUMENTS',
	/** Failed to create document collection */
	SIGNING_COLLECTION_FAILED = 'SIGNING_COLLECTION_FAILED',
	/** Failed to create signing session */
	SIGNING_SESSION_FAILED = 'SIGNING_SESSION_FAILED',
	/** Signicat did not return session URL */
	SIGNING_NO_SESSION_URL = 'SIGNING_NO_SESSION_URL',
	/** Elhub validation errors (e.g. meter failures, PDF download failed) */
	ELHUB_VALIDATION_FAILED = 'ELHUB_VALIDATION_FAILED',
	/** Elhub API failure (401, 400, 5xx after retry) */
	ELHUB_ERROR = 'ELHUB_ERROR'
}